import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FiRefreshCw, FiSearch, FiDollarSign, FiAlertCircle,
  FiCheckCircle, FiClock, FiTrendingDown, FiFileText, FiDownload,
  FiChevronDown, FiChevronRight, FiPrinter, FiTrash2, FiUser
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { useAuth } from '../../../context/AuthContext';
import api, {
  getCxCSummary, getCxCList, getCxCById, cobrarCxC, cobrarMultipleCxC,
  getCobranzasPagos, revertirCobroCxC, getEstadoCuentaCliente,
  getClients, getEmpresaInfo
} from '../../../services/api';
import { exportPDF as exportDeudasPDF, exportExcel as exportDeudasExcel } from '../../../utils/exportCxC';
import { exportPDF as exportCobranzasPDF, exportExcel as exportCobranzasExcel } from '../../../utils/exportCobranzas';
import { imprimirRecibo } from '../../../utils/imprimirRecibo';
import { imprimirEstadoCuenta } from '../../../utils/imprimirEstadoCuenta';
import ClienteFiltroCombo from '../shared/ClienteFiltroCombo';
import ModalDetalleVenta from '../shared/ModalDetalleVenta';
import ModalReenviarWhatsapp from '../shared/ModalReenviarWhatsapp';
import '../../../styles/ModuleTable.css';
import '../../../styles/ComprasMaintenance.css';
import '../Movements/MovimientosMaintenance.css';

// ── helpers ────────────────────────────────────────────────
const fmtS  = n  => `S/ ${parseFloat(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = s => s ? new Date(s).toLocaleDateString('es-PE', { timeZone: 'America/Lima' }) : '—';
// due_date es DATE pura (sin hora, medianoche UTC) — timeZone la correría un
// día atrás; se extrae el día calendario directo del string ISO, sin Date.
const fmtDateOnly = s => { if (!s) return '—'; const m = String(s).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/); return m ? `${m[3]}/${m[2]}/${m[1]}` : String(s).slice(0, 10); };
// Documento fiscal de la venta (serie-secuencia) — no se lleva control con
// el N° Venta interno (VTA-XXXXXX), así que este es el identificador que
// debe verse en listados y tickets.
const docTexto = d => d?.serie ? `${d.document_type || ''} ${d.serie}-${d.sequence}`.trim() : '—';

const PAYMENT_METHODS = [
  { id: 1, name: 'EFECTIVO' },
  { id: 3, name: 'TARJETA CRED. O DEBITO' },
  { id: 4, name: 'TRANSFERENCIA BANCARIA' },
  { id: 5, name: 'PLIN' },
  { id: 6, name: 'YAPE' }
];

// ── KPI card ───────────────────────────────────────────────
function KpiCard({ icon, label, value, color, sub }) {
  const colors = {
    blue:   { bg: '#eff6ff', border: '#bfdbfe', icon: '#3b82f6' },
    orange: { bg: '#fff7ed', border: '#fed7aa', icon: '#d97706' },
    red:    { bg: '#fef2f2', border: '#fecaca', icon: '#dc2626' },
    green:  { bg: '#f0fdf4', border: '#bbf7d0', icon: '#16a34a' }
  };
  const c = colors[color] || colors.blue;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '12px 16px', borderRadius: 8,
      background: c.bg, border: `1px solid ${c.border}`
    }}>
      <div style={{ fontSize: 22, color: c.icon, flexShrink: 0 }}>{icon}</div>
      <div>
        <div style={{ fontSize: 17, fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>{value}</div>
        <div style={{ fontSize: 10.5, color: '#64748b', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</div>
        {sub && <div style={{ fontSize: 10, color: c.icon, fontWeight: 600, marginTop: 1 }}>{sub}</div>}
      </div>
    </div>
  );
}

// ── Badge de estado ────────────────────────────────────────
function BadgeCxC({ status, vencido }) {
  if (status === 'cancelled') {
    return <span className="badge-purchase-status" style={{ background: '#f1f5f9', color: '#64748b', border: '1px solid #cbd5e1' }}>ANULADA</span>;
  }
  if (vencido && status !== 'paid') {
    return <span className="badge-purchase-status badge-cancelled">VENCIDO</span>;
  }
  const cfg = {
    pending: { cls: 'badge-purchase-credit',  label: 'PENDIENTE' },
    partial: { cls: 'badge-purchase-partial', label: 'PARCIAL'   },
    paid:    { cls: 'badge-purchase-paid',    label: 'COBRADO'   }
  };
  const s = cfg[status] || cfg.pending;
  return <span className={`badge-purchase-status ${s.cls}`}>{s.label}</span>;
}

// ── Cabecera + filas anidadas de deudas por cliente ─────────
const DEUDAS_ROWS_HEAD = (
  <tr className="mt-head-row">
    <th>Fecha</th>
    <th>Documento</th>
    <th style={{ textAlign: 'center' }}>Cuota</th>
    <th style={{ textAlign: 'right' }}>Monto</th>
    <th style={{ textAlign: 'right' }}>Cobrado</th>
    <th style={{ textAlign: 'right' }}>Restante</th>
    <th style={{ paddingLeft: 24 }}>Estado</th>
    <th></th>
  </tr>
);

function DeudasRows({ rows, onCobrar, onVerVenta }) {
  return rows.map((r, i) => {
    const isVencido = !!r.vencido && r.status !== 'paid';
    const rowCls = r.status === 'paid'
      ? (i % 2 === 0 ? 'mt-row-even' : 'mt-row-odd')
      : isVencido ? '' : (i % 2 === 0 ? 'mt-row-even' : 'mt-row-odd');

    return (
      <tr key={r.id} className={rowCls} style={isVencido ? { background: '#fff5f5' } : {}}>
        <td style={{ whiteSpace: 'nowrap', fontSize: 11.5 }}>{r.sale_date ? fmtDateOnly(r.sale_date) : fmtDate(r.created_at)}</td>
        <td style={{ fontSize: 11.5, color: '#475569' }}>
          {r.document_type}
          {r.serie && (
            r.sale_id ? (
              <span
                onClick={() => onVerVenta(r.sale_id)}
                style={{ fontFamily: 'monospace', marginLeft: 4, fontSize: 11, color: '#0369a1', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline dotted' }}
                title="Ver detalle de la venta"
              >
                {r.serie}-{r.sequence}
              </span>
            ) : (
              <span style={{ fontFamily: 'monospace', marginLeft: 4, fontSize: 11 }}>{r.serie}-{r.sequence}</span>
            )
          )}
        </td>
        <td style={{ textAlign: 'center' }}>
          <span style={{ background: '#0ea5e9', color: '#fff', fontWeight: 700, fontSize: 10.5, padding: '2px 10px', borderRadius: 20, whiteSpace: 'nowrap' }}>
            CUOTA {r.cuota_num}
          </span>
        </td>
        <td style={{ textAlign: 'right', fontWeight: 600, fontSize: 12.5 }}>{fmtS(r.amount)}</td>
        <td style={{ textAlign: 'right', color: '#16a34a', fontWeight: 600 }}>{fmtS(r.paid_amount)}</td>
        <td style={{ textAlign: 'right', fontWeight: 700, color: parseFloat(r.restante) > 0 ? '#dc2626' : '#16a34a', fontSize: 13 }}>
          {fmtS(r.restante)}
        </td>
        <td style={{ paddingLeft: 24 }}><BadgeCxC status={r.status} vencido={isVencido} /></td>
        <td className="td-actions">
          {r.status !== 'paid' && r.status !== 'cancelled' && (
            <button className="btn-modificar" style={{ padding: '4px 12px', fontSize: 11.5, display: 'flex', alignItems: 'center', gap: 4 }} onClick={() => onCobrar(r.id)}>
              <FiDollarSign size={12} /> Cobrar
            </button>
          )}
          {r.status === 'paid' && (
            <button className="btn-salir" style={{ padding: '4px 10px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }} onClick={() => onCobrar(r.id)}>
              Ver historial
            </button>
          )}
        </td>
      </tr>
    );
  });
}

// ════════════════════════════════════════════════════════════
// Vista de resultado + impresión de ticket, compartida entre
// ModalCobro (una venta) y ModalNuevaCobranza (una o varias) —
// ambas terminan con la misma forma de respuesta del backend
// ({ payment_id, client_name, client_document, detalle: [...] }).
// ════════════════════════════════════════════════════════════
function TicketCobroResult({ ticket, empresa, onCerrar, title = 'COBRO REGISTRADO', showBanner = true }) {
  const { user } = useAuth();
  const totalCobrado = (ticket.detalle || []).reduce((s, d) => s + parseFloat(d.monto_cobrado || 0), 0);

  const handlePrintTicket = (fmt) => {
    imprimirRecibo({
      empresa,
      payment_id:      ticket.payment_id,
      paid_at:         ticket.paid_at || new Date(),
      client_name:     ticket.client_name,
      client_document: ticket.client_document,
      payment_method:  ticket.payment_method,
      username:        user?.full_name || user?.username,
      detalle:         ticket.detalle,
      client_total_debt: ticket.client_total_debt,
      notes:           ticket.notes,
    }, fmt);
  };

  return (
    <div className="mt-modal-overlay" onClick={e => e.target === e.currentTarget && onCerrar()}>
      <div className="mt-modal" style={{ maxWidth: 560 }}>
        <div className="mt-modal-header">
          <span>{title}</span>
          <button className="btn-modal-close" onClick={onCerrar}>✕</button>
        </div>
        <div className="mt-modal-body">
          {showBanner && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14,
              background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6,
              padding: '10px 14px', color: '#16a34a', fontWeight: 700, fontSize: 13
            }}>
              <FiCheckCircle size={16} /> Cobro registrado correctamente
            </div>
          )}

          <table className="mt-table" style={{ fontSize: 12 }}>
            <thead>
              <tr className="mt-head-row">
                <th>Venta</th>
                <th style={{ textAlign: 'right' }}>Deuda</th>
                <th style={{ textAlign: 'right' }}>Cobrado</th>
              </tr>
            </thead>
            <tbody>
              {(ticket.detalle || []).map((d, i) => (
                <tr key={i} className={i % 2 === 0 ? 'mt-row-even' : 'mt-row-odd'}>
                  <td>
                    <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#0369a1' }}>{docTexto(d)}</span>
                    {d.cuota_num && <span style={{ marginLeft: 6, color: '#64748b' }}>Cuota {d.cuota_num}</span>}
                  </td>
                  <td style={{ textAlign: 'right' }}>{fmtS(d.saldo_anterior)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 800, color: parseFloat(d.monto_cobrado) > 0 ? '#16a34a' : '#94a3b8', background: parseFloat(d.monto_cobrado) > 0 ? '#f0fdf4' : undefined }}>{fmtS(d.monto_cobrado)}</td>
                </tr>
              ))}
            </tbody>
            {ticket.detalle?.length > 1 && (
              <tfoot>
                <tr>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>TOTAL COBRADO</td>
                  <td></td>
                  <td style={{ textAlign: 'right', fontWeight: 800, color: '#16a34a', background: '#f0fdf4' }}>{fmtS(totalCobrado)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <div className="mt-modal-footer">
          <button className="btn-modificar" onClick={() => handlePrintTicket('80')}><FiPrinter size={13} /> Imprimir 80mm</button>
          <button className="btn-modificar" onClick={() => handlePrintTicket('60')}><FiPrinter size={13} /> Imprimir 60mm</button>
          <button className="btn-modificar" onClick={() => handlePrintTicket('a4')}><FiPrinter size={13} /> Imprimir A4</button>
          <button className="btn-salir" onClick={onCerrar}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// MODAL DE COBRO (registrar una nueva cobranza de UNA venta)
// ════════════════════════════════════════════════════════════
function ModalCobro({ arId, businessId, empresa, onClose, onCobrado }) {
  const [detail, setDetail]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  // Resultado del cobro recién registrado, listo para mostrar/imprimir el
  // ticket antes de cerrar el modal — null mientras se está llenando el
  // formulario, se llena al confirmar "Registrar Cobro".
  const [ticket, setTicket] = useState(null);

  const [pmId,  setPmId]  = useState(1);
  const [monto, setMonto] = useState('');
  const [notas, setNotas] = useState('');

  useEffect(() => {
    getCxCById(arId, businessId)
      .then(res => {
        setDetail(res.data?.data);
        setMonto(parseFloat(res.data?.data?.restante || 0).toFixed(2));
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [arId, businessId]);

  const handleCobrar = async () => {
    setError('');
    const pm = PAYMENT_METHODS.find(p => p.id === parseInt(pmId));
    setSaving(true);
    try {
      const res = await cobrarCxC(arId, businessId, {
        amount:              parseFloat(monto),
        payment_method_id:   parseInt(pmId),
        payment_method_name: pm?.name || 'EFECTIVO',
        notes:               notas || null
      });
      setTicket({ ...res.data?.data, payment_method: pm?.name || 'EFECTIVO', notes: notas || '' });
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setSaving(false); }
  };

  const restante = detail ? parseFloat(detail.restante) : 0;

  // ── Fase "ticket": el cobro ya se registró, se muestra el detalle y
  // las opciones de impresión antes de cerrar el modal ────────────────
  if (ticket) {
    return <TicketCobroResult ticket={ticket} empresa={empresa} onCerrar={onCobrado} />;
  }

  return (
    <div className="mt-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="mt-modal" style={{ maxWidth: 620 }}>
        <div className="mt-modal-header">
          <span>REGISTRAR COBRANZA</span>
          <button className="btn-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="mt-modal-body">
          {loading ? (
            <div className="mt-loading">Cargando...</div>
          ) : !detail ? (
            <div style={{ color: '#dc2626' }}>No se pudo cargar el registro.</div>
          ) : (
            <>
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 20px',
                background: '#f8fafc', border: '1px solid #e2e8f0',
                borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 12.5
              }}>
                <div>
                  <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: 11 }}>CLIENTE</span>
                  <div style={{ fontWeight: 700, color: '#1e293b' }}>{detail.client_name}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>{detail.client_document}</div>
                </div>
                <div>
                  <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: 11 }}>MOTIVO</span>
                  <div style={{ fontWeight: 600, color: '#0369a1', fontFamily: 'monospace' }}>
                    {detail.sale_number || `CxC-${arId}`}
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>
                    {detail.document_type} {detail.serie ? `${detail.serie}-${detail.sequence}` : ''}
                  </div>
                </div>
                <div>
                  <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: 11 }}>NRO. CUOTA</span>
                  <div>
                    <span style={{ background: '#0ea5e9', color: '#fff', fontWeight: 700, fontSize: 11, padding: '2px 10px', borderRadius: 20, display: 'inline-block' }}>
                      CUOTA {detail.cuota_num}
                    </span>
                  </div>
                </div>
                <div>
                  <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: 11 }}>ESTADO</span>
                  <div>
                    <BadgeCxC status={detail.status} vencido={!!detail.vencido} />
                    {detail.due_date && (
                      <div style={{ fontSize: 10.5, color: detail.vencido ? '#dc2626' : '#64748b', marginTop: 2 }}>
                        Vcto: {fmtDateOnly(detail.due_date)}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: 11 }}>MONTO CUOTA</span>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>{fmtS(detail.amount)}</div>
                </div>
                <div>
                  <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: 11 }}>YA COBRADO</span>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#16a34a' }}>{fmtS(detail.paid_amount)}</div>
                </div>
                <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #e2e8f0', paddingTop: 8 }}>
                  <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: 11 }}>MONTO RESTANTE</span>
                  <div style={{ fontWeight: 800, fontSize: 18, color: '#dc2626' }}>{fmtS(restante)}</div>
                </div>
              </div>

              {detail.status !== 'paid' ? (
                <div className="mt-form-grid">
                  <div className="mt-field">
                    <label>Forma de Pago <span className="req">*</span></label>
                    <select value={pmId} onChange={e => setPmId(e.target.value)}>
                      {PAYMENT_METHODS.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <div className="mt-field">
                    <label>Monto Abono <span className="req">*</span></label>
                    <input type="number" min="0.01" step="0.01" max={restante}
                      value={monto} onChange={e => setMonto(e.target.value)} onFocus={e => e.target.select()} />
                  </div>
                  <div className="mt-field mt-full">
                    <label>Notas / Referencia</label>
                    <input type="text" maxLength={200}
                      value={notas} onChange={e => setNotas(e.target.value)}
                      placeholder="Ej: Transferencia banco BCP, Voucher #1234..." />
                  </div>
                  <div className="mt-field mt-full">
                    <div style={{ display: 'flex', justifyContent: 'space-between', background: '#fef9c3', border: '1px solid #fde047', borderRadius: 6, padding: '8px 12px', fontSize: 12.5 }}>
                      <span style={{ fontWeight: 600, color: '#78350f' }}>Restante después del cobro:</span>
                      <span style={{ fontWeight: 800, color: '#dc2626' }}>
                        {fmtS(Math.max(0, restante - parseFloat(monto || 0)))}
                      </span>
                    </div>
                  </div>
                  {error && (
                    <div className="mt-field mt-full" style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '8px 12px', color: '#dc2626', fontSize: 12.5, fontWeight: 600 }}>
                      ⚠ {error}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '12px 16px', color: '#16a34a', fontWeight: 700, textAlign: 'center', fontSize: 13 }}>
                  ✓ Esta deuda está completamente cobrada.
                </div>
              )}

              {detail.payments?.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                    Historial de Cobros
                  </div>
                  <table className="mt-table" style={{ fontSize: 12 }}>
                    <thead>
                      <tr className="mt-head-row">
                        <th>#</th><th>Fecha</th><th>Forma de Pago</th>
                        <th style={{ textAlign: 'right' }}>Monto Cobrado</th><th>Notas</th><th>Usuario</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.payments.map((p, i) => {
                        const anulado = !!p.anulado;
                        const rowStyle = anulado
                          ? { background: '#fff1f1', color: '#94a3b8', textDecoration: 'line-through', textDecorationColor: '#fca5a5' }
                          : {};
                        return (
                          <tr key={p.id} className={i % 2 === 0 ? 'mt-row-even' : 'mt-row-odd'} style={rowStyle}>
                            <td style={{ color: '#94a3b8', fontSize: 11, textDecoration: 'none' }}>{i + 1}</td>
                            <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(p.paid_at)}</td>
                            <td style={{ textDecoration: 'none' }}>
                              {anulado ? (
                                <span className="badge-purchase-status" style={{ background: '#f1f5f9', color: '#64748b', border: '1px solid #cbd5e1' }}>ANULADO</span>
                              ) : (
                                <span className="badge-purchase-status badge-purchase-paid">{p.payment_method_short || p.payment_method_name || '—'}</span>
                              )}
                            </td>
                            <td style={{ textAlign: 'right', fontWeight: 700, color: anulado ? undefined : '#16a34a' }}>{fmtS(p.amount)}</td>
                            <td style={{ fontSize: 11.5, color: anulado ? undefined : '#64748b' }}>{p.notes || '—'}</td>
                            <td style={{ fontSize: 11.5 }}>{p.username || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
        <div className="mt-modal-footer">
          {detail && detail.status !== 'paid' && (
            <button className="btn-modificar" onClick={handleCobrar} disabled={saving || !monto}>
              {saving ? 'Registrando...' : `Registrar Cobro de ${fmtS(monto || 0)}`}
            </button>
          )}
          <button className="btn-salir" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// MODAL NUEVA COBRANZA — cobra una o varias ventas/cuotas de UN
// cliente en un solo recibo (backend: CuentaCobrar.cobrarMultiple)
// ════════════════════════════════════════════════════════════
function ModalNuevaCobranza({ businessId, empresa, clients, onClose, onCobrado }) {
  const [clientId,      setClientId]      = useState('');
  const [deudas,        setDeudas]        = useState([]);
  const [loadingDeudas, setLoadingDeudas] = useState(false);
  const [modo,          setModo]          = useState('auto'); // 'auto' | 'manual'
  const [montoUnico,    setMontoUnico]    = useState('');
  const [montos,        setMontos]        = useState({}); // ar_id -> string (modo manual)
  const [pmId,          setPmId]          = useState(1);
  const [notas,         setNotas]         = useState('');
  const [saving,        setSaving]        = useState(false);
  const [error,         setError]         = useState('');
  const [ticket,        setTicket]        = useState(null);

  useEffect(() => {
    if (!clientId) { setDeudas([]); setMontos({}); setMontoUnico(''); return; }
    setLoadingDeudas(true);
    setError('');
    getCxCList(businessId, { client_id: clientId, status: 'con_deuda', page: 1, limit: 999 })
      .then(res => {
        // Del más antiguo al más reciente — así el modo "monto único" reparte
        // en el orden correcto (FIFO) sin depender del orden que devuelva la API.
        const rows = (res.data?.rows || [])
          .slice()
          .sort((a, b) => new Date(a.sale_date || a.created_at) - new Date(b.sale_date || b.created_at));
        setDeudas(rows);
        setMontos({});
        setMontoUnico('');
      })
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoadingDeudas(false));
  }, [clientId, businessId]);

  const totalDeuda = deudas.reduce((s, d) => s + parseFloat(d.restante || 0), 0);

  // Distribución efectiva por ar_id según el modo activo — en 'auto' se
  // reparte el monto único de la venta más antigua a la más reciente hasta
  // agotarlo; en 'manual' se usa directamente lo tecleado por fila, siempre
  // acotado al restante de esa venta (así la suma nunca puede superar la
  // deuda total del cliente en ninguno de los dos modos).
  const distribucion = useMemo(() => {
    const map = {};
    if (modo === 'auto') {
      let disponible = Math.min(parseFloat(montoUnico) || 0, totalDeuda);
      for (const d of deudas) {
        const asignado = disponible > 0 ? Math.min(disponible, parseFloat(d.restante)) : 0;
        map[d.id] = asignado;
        disponible -= asignado;
      }
    } else {
      for (const d of deudas) {
        map[d.id] = Math.min(parseFloat(montos[d.id]) || 0, parseFloat(d.restante));
      }
    }
    return map;
  }, [modo, montoUnico, montos, deudas, totalDeuda]);

  const totalAsignado = Object.values(distribucion).reduce((s, v) => s + v, 0);

  const handleMontoUnicoChange = (val) => {
    const n = parseFloat(val);
    setMontoUnico(!isNaN(n) && n > totalDeuda ? totalDeuda.toFixed(2) : val);
  };

  const handleMontoManualChange = (arId, val, restanteFila) => {
    const n = parseFloat(val);
    setMontos(m => ({ ...m, [arId]: (!isNaN(n) && n > restanteFila) ? restanteFila.toFixed(2) : val }));
  };

  const handleCobrar = async () => {
    setError('');
    const pm = PAYMENT_METHODS.find(p => p.id === parseInt(pmId));
    const dist = Object.entries(distribucion)
      .filter(([, v]) => v > 0)
      .map(([ar_id, amount]) => ({ ar_id: parseInt(ar_id), amount }));
    if (dist.length === 0) { setError('Ingresa al menos un monto a abonar'); return; }
    setSaving(true);
    try {
      const res = await cobrarMultipleCxC(businessId, {
        client_id:            parseInt(clientId),
        payment_method_id:    parseInt(pmId),
        payment_method_name:  pm?.name || 'EFECTIVO',
        notes:                notas || null,
        distribucion:         dist,
      });
      setTicket({ ...res.data?.data, payment_method: pm?.name || 'EFECTIVO', notes: notas || '' });
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setSaving(false); }
  };

  if (ticket) {
    return <TicketCobroResult ticket={ticket} empresa={empresa} onCerrar={onCobrado} />;
  }

  return (
    <div className="mt-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="mt-modal" style={{ maxWidth: 760 }}>
        <div className="mt-modal-header">
          <span>NUEVA COBRANZA</span>
          <button className="btn-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="mt-modal-body">
          <div className="mt-field mt-full" style={{ marginBottom: 14 }}>
            <label>Cliente <span className="req">*</span></label>
            <ClienteFiltroCombo clients={clients} value={clientId} onChange={setClientId} placeholder="Buscar cliente..." />
          </div>

          {!clientId ? (
            <div style={{ color: '#94a3b8', fontStyle: 'italic', padding: '20px 0', textAlign: 'center' }}>
              Selecciona un cliente para ver sus deudas
            </div>
          ) : loadingDeudas ? (
            <div className="mt-loading">Cargando deudas...</div>
          ) : deudas.length === 0 ? (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '12px 16px', color: '#16a34a', fontWeight: 700, textAlign: 'center', fontSize: 13 }}>
              ✓ Este cliente no tiene deudas pendientes.
            </div>
          ) : (
            <>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6,
                padding: '8px 14px', marginBottom: 12, fontSize: 12.5
              }}>
                <span style={{ fontWeight: 600, color: '#7f1d1d' }}>Deuda total del cliente:</span>
                <span style={{ fontWeight: 800, fontSize: 15, color: '#dc2626' }}>{fmtS(totalDeuda)}</span>
              </div>

              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <button type="button" className={modo === 'auto' ? 'btn-nuevo' : 'btn-salir'} onClick={() => setModo('auto')}>
                  Monto único (reparte automático)
                </button>
                <button type="button" className={modo === 'manual' ? 'btn-nuevo' : 'btn-salir'} onClick={() => setModo('manual')}>
                  Distribuir manualmente
                </button>
              </div>

              {modo === 'auto' && (
                <div className="cob-monto-unico-card" style={{ marginBottom: 14 }}>
                  <label className="cob-monto-unico-lbl">
                    Monto a abonar — se reparte de la venta más antigua a la más reciente
                  </label>
                  <div className="cob-monto-unico-wrap">
                    <span className="cob-monto-unico-prefix">S/</span>
                    <input
                      className="cob-monto-unico-input"
                      type="number" min="0" step="0.01" max={totalDeuda}
                      value={montoUnico}
                      onChange={e => handleMontoUnicoChange(e.target.value)}
                      onFocus={e => e.target.select()}
                      placeholder="0.00"
                      autoFocus
                    />
                  </div>
                </div>
              )}

              <table className="mt-table" style={{ fontSize: 12 }}>
                <thead>
                  <tr className="mt-head-row">
                    <th>Fecha</th><th>Documento</th><th style={{ textAlign: 'center' }}>Cuota</th>
                    <th style={{ textAlign: 'right' }}>Deuda</th>
                    <th style={{ textAlign: 'right' }}>Abono</th>
                  </tr>
                </thead>
                <tbody>
                  {deudas.map((d, i) => (
                    <tr key={d.id} className={i % 2 === 0 ? 'mt-row-even' : 'mt-row-odd'}>
                      <td style={{ whiteSpace: 'nowrap', fontSize: 11.5 }}>{d.sale_date ? fmtDateOnly(d.sale_date) : fmtDate(d.created_at)}</td>
                      <td><span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#0369a1' }}>{docTexto(d)}</span></td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{ background: '#0ea5e9', color: '#fff', fontWeight: 700, fontSize: 10.5, padding: '2px 10px', borderRadius: 20 }}>
                          CUOTA {d.cuota_num}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>{fmtS(d.restante)}</td>
                      <td style={{ textAlign: 'right' }}>
                        {modo === 'auto' ? (
                          <span style={{ fontWeight: 800, color: distribucion[d.id] > 0 ? '#16a34a' : '#94a3b8' }}>
                            {fmtS(distribucion[d.id] || 0)}
                          </span>
                        ) : (
                          <input
                            type="number" min="0" step="0.01" max={d.restante}
                            style={{ width: 90, textAlign: 'right' }}
                            value={montos[d.id] ?? ''}
                            onChange={e => handleMontoManualChange(d.id, e.target.value, parseFloat(d.restante))}
                            onFocus={e => e.target.select()}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3} style={{ textAlign: 'right', fontWeight: 700 }}>TOTAL A ABONAR</td>
                    <td></td>
                    <td style={{ textAlign: 'right', fontWeight: 800, fontSize: 13.5, color: '#16a34a', background: '#f0fdf4' }}>
                      {fmtS(totalAsignado)}
                    </td>
                  </tr>
                </tfoot>
              </table>

              <div className="mt-form-grid" style={{ marginTop: 14 }}>
                <div className="mt-field">
                  <label>Forma de Pago <span className="req">*</span></label>
                  <select value={pmId} onChange={e => setPmId(e.target.value)}>
                    {PAYMENT_METHODS.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div className="mt-field mt-full">
                  <label>Notas / Referencia</label>
                  <input type="text" maxLength={200}
                    value={notas} onChange={e => setNotas(e.target.value)}
                    placeholder="Ej: Transferencia banco BCP, Voucher #1234..." />
                </div>
              </div>

              {error && (
                <div style={{ marginTop: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '8px 12px', color: '#dc2626', fontSize: 12.5, fontWeight: 600 }}>
                  ⚠ {error}
                </div>
              )}
            </>
          )}
        </div>
        <div className="mt-modal-footer">
          {clientId && deudas.length > 0 && (
            <button className="btn-modificar" onClick={handleCobrar} disabled={saving || totalAsignado <= 0}>
              {saving ? 'Registrando...' : `Registrar Cobro de ${fmtS(totalAsignado)}`}
            </button>
          )}
          <button className="btn-salir" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// TAB: DEUDAS POR CLIENTE
// ════════════════════════════════════════════════════════════
function TabDeudas({ businessId, empresa, clients, onOpenCobro }) {
  const [summary,   setSummary]   = useState(null);
  const [rows,      setRows]      = useState([]);
  const [total,     setTotal]     = useState(0);
  const [loading,   setLoading]   = useState(false);
  const [filters, setFilters] = useState({ client_id: '', status: '', from_date: '', to_date: '', search: '', overdue_only: false });
  const [searchText,   setSearchText]   = useState('');
  const [exporting,    setExporting]    = useState(false);
  const [appliedDates, setAppliedDates] = useState({ from_date: '', to_date: '' });
  const [expandedClients, setExpandedClients] = useState(new Set());
  const [ventaModal, setVentaModal] = useState(null);
  const [waCliente, setWaCliente] = useState(null); // { client_id, client_name } — para modal "Reenviar a..."
  const [ecData, setEcData] = useState(null); // { empresa, client, debts, totals } — pendiente de elegir formato de impresión

  const loadSummary = useCallback(async (dates = {}) => {
    if (!businessId) return;
    try {
      const res = await getCxCSummary(businessId, { from_date: dates.from_date || null, to_date: dates.to_date || null });
      setSummary(res.data?.data || null);
    } catch {}
  }, [businessId]);

  const loadList = useCallback(async (f) => {
    if (!businessId) return;
    setLoading(true);
    const ef = f || filters;
    try {
      const res = await getCxCList(businessId, { ...ef, page: 1, limit: 9999 });
      setRows(res.data?.rows || []);
      setTotal(res.data?.total || 0);
    } catch {} finally { setLoading(false); }
  }, [businessId, filters]);

  const reload = useCallback(() => { loadSummary(appliedDates); loadList(); }, [loadSummary, loadList, appliedDates]);

  useEffect(() => { if (businessId) { loadSummary(); loadList(); } }, [businessId]); // eslint-disable-line react-hooks/exhaustive-deps

  const groups = useMemo(() => {
    const map = new Map();
    rows.forEach(r => {
      const key = r.client_id || 'sin-cliente';
      if (!map.has(key)) map.set(key, { key, client_id: r.client_id, client_name: r.client_name || 'CLIENTES VARIOS', client_document: r.client_document || '—', rows: [] });
      map.get(key).rows.push(r);
    });
    return Array.from(map.values()).map(g => {
      const activos = g.rows.filter(r => r.status !== 'cancelled');
      return {
        ...g,
        count: activos.length,
        total_monto:    activos.reduce((s, r) => s + parseFloat(r.amount || 0), 0),
        total_cobrado:  activos.reduce((s, r) => s + parseFloat(r.paid_amount || 0), 0),
        total_restante: activos.filter(r => r.status !== 'paid').reduce((s, r) => s + parseFloat(r.restante || 0), 0),
      };
    }).sort((a, b) => a.client_name.localeCompare(b.client_name));
  }, [rows]);

  useEffect(() => { setExpandedClients(new Set(groups.map(g => g.key))); }, [groups]);
  const toggleClient = (key) => setExpandedClients(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });

  const handleFiltrar = () => {
    const f = { ...filters, search: searchText };
    setFilters(f);
    const dates = { from_date: filters.from_date, to_date: filters.to_date };
    setAppliedDates(dates);
    loadList(f);
    loadSummary(dates);
  };

  const handleVerEstadoCuenta = async (clientId) => {
    if (!clientId) return;
    try {
      const res = await getEstadoCuentaCliente(clientId, businessId);
      setEcData({ empresa, ...res.data.data });
    } catch (e) {
      alert(e.response?.data?.error || 'No se pudo generar el estado de cuenta');
    }
  };

  const handlePrintEstadoCuenta = (fmt) => {
    imprimirEstadoCuenta(ecData, fmt);
    setEcData(null);
  };

  const handleWhatsappEstadoCuenta = async (clientId, clientName) => {
    if (!clientId) return;
    try {
      const res = await api.get('/api/whatsapp/status');
      if (res.data.data?.status !== 'connected') {
        alert('WhatsApp no está vinculado. Ve a Configuración → WhatsApp para vincularlo.');
        return;
      }
    } catch {
      alert('No se pudo verificar el estado de WhatsApp.');
      return;
    }
    setWaCliente({ client_id: clientId, client_name: clientName });
  };

  const handleExport = async (format) => {
    if (exporting) return;
    setExporting(true);
    try {
      const args = { empresa, data: rows, usuario: null, summary };
      if (format === 'pdf')   exportDeudasPDF(args);
      if (format === 'excel') exportDeudasExcel(args);
    } catch (e) { console.error(e); } finally { setExporting(false); }
  };

  const hasDateFilter = !!(appliedDates.from_date || appliedDates.to_date);

  return (
    <>
      <div className="mt-toolbar">
        <div className="mt-toolbar-left"><span className="mt-title">DEUDAS POR CLIENTE</span></div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-nuevo" style={{ background: '#475569' }} onClick={reload}><FiRefreshCw style={{ fontSize: 12 }} /> Actualizar</button>
          <button className="btn-export-pdf" onClick={() => handleExport('pdf')} disabled={exporting} title="Exportar PDF"><FiFileText size={13} /> {exporting ? '...' : 'PDF'}</button>
          <button className="btn-export-excel" onClick={() => handleExport('excel')} disabled={exporting} title="Exportar Excel"><FiDownload size={13} /> {exporting ? '...' : 'Excel'}</button>
        </div>
      </div>

      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: hasDateFilter ? 'repeat(4, 1fr)' : 'repeat(2, 1fr)', gap: 12, padding: '12px 16px 4px' }}>
          {hasDateFilter && <KpiCard icon={<FiTrendingDown />} color="blue" label="Total Deuda" value={fmtS(summary.total_deuda)} />}
          <KpiCard icon={<FiClock />} color="orange" label="Pendiente de Cobro" value={fmtS(summary.total_pendiente)} sub={`${+summary.cnt_pending + +summary.cnt_partial} activos`} />
          <KpiCard icon={<FiAlertCircle />} color="red" label="Deuda Vencida" value={fmtS(summary.total_vencido)} sub={`${summary.cnt_vencido} vencidos`} />
          {hasDateFilter && <KpiCard icon={<FiCheckCircle />} color="green" label="Total Cobrado" value={fmtS(summary.total_cobrado)} />}
        </div>
      )}

      <div className="si-filter-bar">
        <div className="si-filter-group">
          <ClienteFiltroCombo clients={clients} value={filters.client_id} onChange={id => setFilters(f => ({ ...f, client_id: id }))} />
          <select className="si-select" value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
            <option value="">Todos (sin anuladas)</option>
            <option value="con_deuda">Con Deuda (pendiente + parcial)</option>
            <option value="pending">Solo pendiente (sin abonar)</option>
            <option value="partial">Parcialmente cobrado</option>
            <option value="paid">Cobrado completo</option>
            <option value="cancelled">Anuladas (venta cancelada)</option>
          </select>
          <span className="si-label">Desde:</span>
          <input type="date" className="si-input-date" value={filters.from_date} onChange={e => setFilters(f => ({ ...f, from_date: e.target.value }))} />
          <span className="si-label">Hasta:</span>
          <input type="date" className="si-input-date" value={filters.to_date} onChange={e => setFilters(f => ({ ...f, to_date: e.target.value }))} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#475569', cursor: 'pointer' }}>
            <input type="checkbox" checked={filters.overdue_only} onChange={e => setFilters(f => ({ ...f, overdue_only: e.target.checked }))} /> Solo vencidos
          </label>
          <div className="si-search-wrap">
            <FiSearch className="si-search-icon" />
            <input className="si-search" placeholder="Cliente, N° venta..." value={searchText} onChange={e => setSearchText(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleFiltrar()} />
          </div>
          <button className="btn-nuevo" onClick={handleFiltrar} disabled={loading}><FiRefreshCw style={{ fontSize: 12 }} /> Filtrar</button>
        </div>
      </div>

      <div className="mt-export-bar" style={{ justifyContent: 'space-between' }}>
        <span style={{ fontSize: 12, color: '#64748b' }}>{loading ? 'Cargando...' : `${total} registro(s)`}</span>
      </div>

      <div className="mt-table-wrap">
        {loading ? (
          <table className="mt-table"><tbody><tr className="mt-empty"><td>Cargando...</td></tr></tbody></table>
        ) : groups.length === 0 ? (
          <table className="mt-table"><tbody><tr className="mt-empty"><td>Sin registros para los filtros seleccionados</td></tr></tbody></table>
        ) : (
          <table className="mt-table mt-table-grouped">
            <thead>
              <tr className="mt-head-row">
                <th style={{ width: 30 }}></th>
                <th>Cliente</th>
                <th>DNI/RUC</th>
                <th style={{ textAlign: 'right' }}>N° Cuotas</th>
                <th style={{ textAlign: 'right' }}>Monto Total</th>
                <th style={{ textAlign: 'right' }}>Cobrado</th>
                <th style={{ textAlign: 'right' }}>Restante</th>
                <th style={{ width: 210 }}></th>
              </tr>
            </thead>
            <tbody>
              {groups.map(g => {
                const isOpen = expandedClients.has(g.key);
                return (
                  <React.Fragment key={g.key}>
                    <tr className="mt-group-row" onClick={() => toggleClient(g.key)} title="Click para desglosar">
                      <td>{isOpen ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}</td>
                      <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={g.client_name}>{g.client_name}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 11.5 }}>{g.client_document}</td>
                      <td style={{ textAlign: 'right' }}>{g.count}</td>
                      <td style={{ textAlign: 'right' }}>{fmtS(g.total_monto)}</td>
                      <td style={{ textAlign: 'right', color: '#16a34a' }}>{fmtS(g.total_cobrado)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: g.total_restante > 0 ? '#dc2626' : '#16a34a' }}>{fmtS(g.total_restante)}</td>
                      <td style={{ display: 'flex', gap: 6 }}>
                        {g.client_id && (
                          <>
                            <button
                              className="btn-salir"
                              style={{ padding: '3px 10px', fontSize: 10.5, display: 'flex', alignItems: 'center', gap: 4 }}
                              onClick={e => { e.stopPropagation(); handleVerEstadoCuenta(g.client_id); }}
                              title="Ver / imprimir estado de cuenta"
                            >
                              <FiUser size={11} /> Estado de Cuenta
                            </button>
                            <button
                              className="btn-salir"
                              style={{ padding: '3px 10px', fontSize: 10.5, display: 'flex', alignItems: 'center', gap: 4, color: '#25d366', borderColor: '#bbf7d0' }}
                              onClick={e => { e.stopPropagation(); handleWhatsappEstadoCuenta(g.client_id, g.client_name); }}
                              title="Enviar Estado de Cuenta por WhatsApp"
                            >
                              <FaWhatsapp size={11} /> Enviar
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="mt-group-detail">
                        <td colSpan={8}>
                          <table className="mt-table mt-table-nested">
                            <thead>{DEUDAS_ROWS_HEAD}</thead>
                            <tbody><DeudasRows rows={g.rows} onCobrar={onOpenCobro} onVerVenta={setVentaModal} /></tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {ventaModal && (
        <ModalDetalleVenta saleId={ventaModal} businessId={businessId} onClose={() => setVentaModal(null)} />
      )}

      {waCliente && (
        <ModalReenviarWhatsapp
          title={`Enviar Estado de Cuenta — ${waCliente.client_name}`}
          sendEndpoint="/api/whatsapp/send-estado-cuenta"
          sendBody={{ clientId: waCliente.client_id }}
          onClose={() => setWaCliente(null)}
        />
      )}

      {ecData && (
        <div className="mt-modal-overlay" onClick={e => e.target === e.currentTarget && setEcData(null)}>
          <div className="mt-modal" style={{ maxWidth: 380 }}>
            <div className="mt-modal-header">
              <span>Estado de Cuenta — {ecData.client?.client_name}</span>
              <button className="btn-modal-close" onClick={() => setEcData(null)}>✕</button>
            </div>
            <div className="mt-modal-body">
              <p style={{ margin: '0 0 12px', fontSize: 13, color: '#475569' }}>Elige el formato de impresión:</p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn-modificar" style={{ flex: 1, justifyContent: 'center' }} onClick={() => handlePrintEstadoCuenta('a4')}>
                  <FiPrinter size={13} /> A4
                </button>
                <button className="btn-modificar" style={{ flex: 1, justifyContent: 'center' }} onClick={() => handlePrintEstadoCuenta('80')}>
                  <FiPrinter size={13} /> 80mm
                </button>
              </div>
            </div>
            <div className="mt-modal-footer">
              <button className="btn-salir" onClick={() => setEcData(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════
// TAB: COBRANZAS REALIZADAS
// ════════════════════════════════════════════════════════════
function TabRealizadas({ businessId, empresa, clients }) {
  const [rows,    setRows]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ client_id: '', payment_method_id: '', from_date: '', to_date: '', search: '' });
  const [searchText, setSearchText] = useState('');
  const [page,     setPage]     = useState(1);
  const [perPage,  setPerPage]  = useState(50);
  const [exporting, setExporting] = useState(false);
  const [deleting,  setDeleting]  = useState(null);
  const [reprintTicket, setReprintTicket] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const toggleExpand = id => setExpandedRows(s => {
    const next = new Set(s);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const loadList = useCallback(async (f, p) => {
    if (!businessId) return;
    setLoading(true);
    try {
      const res = await getCobranzasPagos(businessId, { ...(f || filters), page: p || page, limit: perPage });
      setRows(res.data?.rows || []);
      setTotal(res.data?.total || 0);
    } catch {} finally { setLoading(false); }
  }, [businessId, filters, page, perPage]);

  useEffect(() => { if (businessId) loadList(); }, [businessId, page, perPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFiltrar = () => {
    const f = { ...filters, search: searchText };
    setFilters(f);
    setPage(1);
    loadList(f, 1);
  };

  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const totalPagina = rows.reduce((s, r) => s + parseFloat(r.amount || 0), 0);

  const handleReimprimir = async (row) => {
    // `detalle_snapshot` se calcula y graba UNA sola vez al momento del cobro
    // (ver CuentaCobrar.cobrar/cobrarMultiple) — incluye TANTO las ventas
    // cobradas como las demás deudas del cliente que existían en ese instante
    // (con Cobrado=0). Reimprimir solo LEE ese JSON, nunca lo recalcula: así
    // el recibo no cambia si después el cliente paga, se le anula otra venta,
    // o genera una nueva deuda — siempre muestra la foto real de ese momento.
    if (row.detalle_snapshot) {
      const detalle = typeof row.detalle_snapshot === 'string' ? JSON.parse(row.detalle_snapshot) : row.detalle_snapshot;
      setReprintTicket({
        payment_id:      row.id,
        paid_at:         row.paid_at,
        client_name:     row.client_name,
        client_document: row.client_document,
        payment_method:  row.payment_method_name,
        username:        row.username,
        notes:           row.notes || '',
        client_total_debt: row.client_total_debt_snapshot,
        detalle,
      });
      return;
    }

    // Fallback SOLO para cobranzas anteriores a que existiera `detalle_snapshot`
    // (backfilleadas desde el esquema viejo, sin esta columna) — se reconstruye
    // en vivo lo mejor posible. Comparación de instante completo (fecha + hora
    // vía getTime(), no solo el día) para no incluir ventas que en ese momento
    // exacto todavía no existían.
    const propias = (row.ventas || []).map(v => ({
      sale_number:    v.sale_number,
      document_type:  v.document_type,
      serie:          v.serie,
      sequence:       v.sequence,
      sale_date:      v.sale_date,
      cuota_num:      v.cuota_num,
      saldo_anterior: v.saldo_anterior ?? '',
      monto_cobrado:  v.amount,
      saldo_actual:   v.saldo_actual ?? '',
    }));
    const arIdsPropios = new Set((row.ventas || []).map(v => v.ar_id));

    let otras = [];
    try {
      const res = await getCxCList(businessId, { client_id: row.client_id, status: 'con_deuda', page: 1, limit: 999 });
      const cobroTime = new Date(row.paid_at).getTime();
      // Comparación de instante exacto (no solo el día) — usa ar.created_at,
      // no sale_date (que ahora es solo la fecha calendario sin hora, no
      // sirve para saber si el CxC ya existía o no al momento exacto del cobro).
      otras = (res.data?.rows || [])
        .filter(d => !arIdsPropios.has(d.id) && new Date(d.created_at).getTime() <= cobroTime)
        .map(d => ({
          sale_number:    d.sale_number,
          document_type:  d.document_type,
          serie:          d.serie,
          sequence:       d.sequence,
          sale_date:      d.sale_date,
          cuota_num:      d.cuota_num,
          saldo_anterior: d.restante,
          monto_cobrado:  0,
          saldo_actual:   d.restante,
        }));
    } catch { /* si falla, el recibo igual muestra las ventas de este cobro */ }

    setReprintTicket({
      payment_id:      row.id,
      paid_at:         row.paid_at,
      client_name:     row.client_name,
      client_document: row.client_document,
      payment_method:  row.payment_method_name,
      username:        row.username,
      notes:           row.notes || '',
      client_total_debt: row.client_total_debt_snapshot,
      detalle: [...propias, ...otras],
    });
  };

  const handleEliminar = async (row) => {
    const nVentas = row.ventas?.length || 0;
    const msg = nVentas > 1
      ? `¿Eliminar esta cobranza de ${fmtS(row.amount)} a ${row.client_name}? Afecta ${nVentas} ventas — esta acción no se puede deshacer y devolverá el saldo a la deuda de TODAS ellas.`
      : `¿Eliminar el cobro de ${fmtS(row.amount)} a ${row.client_name}? Esta acción no se puede deshacer y devolverá el saldo a la deuda.`;
    if (!window.confirm(msg)) return;
    setDeleting(row.id);
    try {
      await revertirCobroCxC(row.id, businessId);
      await loadList();
    } catch (e) {
      alert(e.response?.data?.error || 'No se pudo eliminar el cobro');
    } finally { setDeleting(null); }
  };

  const handleExport = async (format) => {
    if (exporting) return;
    setExporting(true);
    try {
      const res = await getCobranzasPagos(businessId, { ...filters, search: searchText, page: 1, limit: 9999 });
      const allData = res.data?.rows || [];
      const totalCobrado = allData.reduce((s, r) => s + parseFloat(r.amount || 0), 0);
      const args = { empresa, data: allData, usuario: null, totalCobrado };
      if (format === 'pdf')   exportCobranzasPDF(args);
      if (format === 'excel') exportCobranzasExcel(args);
    } catch (e) { console.error(e); } finally { setExporting(false); }
  };

  return (
    <>
      <div className="mt-toolbar">
        <div className="mt-toolbar-left"><span className="mt-title">COBRANZAS REALIZADAS</span></div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-nuevo" style={{ background: '#475569' }} onClick={() => loadList()}><FiRefreshCw style={{ fontSize: 12 }} /> Actualizar</button>
          <button className="btn-export-pdf" onClick={() => handleExport('pdf')} disabled={exporting} title="Exportar PDF"><FiFileText size={13} /> {exporting ? '...' : 'PDF'}</button>
          <button className="btn-export-excel" onClick={() => handleExport('excel')} disabled={exporting} title="Exportar Excel"><FiDownload size={13} /> {exporting ? '...' : 'Excel'}</button>
        </div>
      </div>

      <div className="si-filter-bar">
        <div className="si-filter-group">
          <ClienteFiltroCombo clients={clients} value={filters.client_id} onChange={id => setFilters(f => ({ ...f, client_id: id }))} />
          <select className="si-select" value={filters.payment_method_id} onChange={e => setFilters(f => ({ ...f, payment_method_id: e.target.value }))}>
            <option value="">Todas las formas de pago</option>
            {PAYMENT_METHODS.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <span className="si-label">Desde:</span>
          <input type="date" className="si-input-date" value={filters.from_date} onChange={e => setFilters(f => ({ ...f, from_date: e.target.value }))} />
          <span className="si-label">Hasta:</span>
          <input type="date" className="si-input-date" value={filters.to_date} onChange={e => setFilters(f => ({ ...f, to_date: e.target.value }))} />
          <div className="si-search-wrap">
            <FiSearch className="si-search-icon" />
            <input className="si-search" placeholder="Cliente, N° venta..." value={searchText} onChange={e => setSearchText(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleFiltrar()} />
          </div>
          <button className="btn-nuevo" onClick={handleFiltrar} disabled={loading}><FiRefreshCw style={{ fontSize: 12 }} /> Filtrar</button>
        </div>
      </div>

      <div className="mt-export-bar" style={{ justifyContent: 'space-between' }}>
        <span style={{ fontSize: 12, color: '#64748b' }}>{loading ? 'Cargando...' : `${total} cobro(s)`}</span>
        <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 700 }}>Total cobrado en esta página: {fmtS(totalPagina)}</span>
        <div className="mt-pagination">
          <button onClick={() => setPage(1)} disabled={page === 1}>|&lt;</button>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>&lt;</button>
          <span className="pg-current">{page}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>&gt;</button>
          <button onClick={() => setPage(totalPages)} disabled={page >= totalPages}>&gt;|</button>
          <span className="pg-info">de {totalPages}</span>
          <select value={perPage} onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}>
            {[25, 50, 100].map(n => <option key={n} value={n}>{n}/pág</option>)}
          </select>
        </div>
      </div>

      <div className="mt-table-wrap">
        <table className="mt-table">
          <thead>
            <tr className="mt-head-row">
              <th></th>
              <th>Fecha</th>
              <th>N° Cobranza</th>
              <th>Cliente</th>
              <th>DNI/RUC</th>
              <th>Ventas</th>
              <th>Forma de Pago</th>
              <th style={{ textAlign: 'right' }}>Monto</th>
              <th>Notas</th>
              <th>Usuario</th>
              <th style={{ width: 110 }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr className="mt-empty"><td colSpan={11}>Cargando...</td></tr>
            ) : rows.length === 0 ? (
              <tr className="mt-empty"><td colSpan={11}>Sin cobros para los filtros seleccionados</td></tr>
            ) : rows.map((r, i) => {
              const anulado = !!r.anulado;
              const ventas = r.ventas || [];
              // `detalle_snapshot` es el registro COMPLETO de la deuda del cliente al
              // momento de la cobranza (ventas pagadas + las que se dejaron sin afectar
              // a propósito, con monto_cobrado=0) — mismo dato que ya usa el ticket
              // impreso. El listado debe mostrar este panorama completo, no solo las
              // líneas que sí tuvieron abono. Fallback a `ventas` (solo pagadas) para
              // cobranzas antiguas backfilleadas sin esta columna.
              const snap = r.detalle_snapshot
                ? (typeof r.detalle_snapshot === 'string' ? JSON.parse(r.detalle_snapshot) : r.detalle_snapshot)
                : ventas.map(v => ({ ...v, monto_cobrado: v.amount }));
              const expandible = snap.length > 1;
              const expanded = expandedRows.has(r.id);
              const rowStyle = anulado
                ? { background: '#fff1f1', color: '#94a3b8', textDecoration: 'line-through', textDecorationColor: '#fca5a5' }
                : {};
              return (
                <React.Fragment key={r.id}>
                  <tr className={i % 2 === 0 ? 'mt-row-even' : 'mt-row-odd'} style={rowStyle}>
                    <td style={{ textAlign: 'center' }}>
                      {expandible && (
                        <button
                          onClick={() => toggleExpand(r.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: anulado ? '#94a3b8' : '#0369a1', display: 'flex' }}
                          title={expanded ? 'Ocultar ventas' : 'Ver ventas'}
                        >
                          {expanded ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                        </button>
                      )}
                    </td>
                    <td style={{ whiteSpace: 'nowrap', fontSize: 11.5 }}>{fmtDate(r.paid_at)}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 11.5, fontWeight: 700 }}>{r.id}</td>
                    <td style={{ fontWeight: 600, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.client_name}>{r.client_name}</td>
                    <td style={{ fontSize: 11.5, fontFamily: 'monospace', color: anulado ? undefined : '#64748b' }}>{r.client_document || '—'}</td>
                    <td>
                      {!expandible ? (
                        <span style={{ fontFamily: 'monospace', fontSize: 11.5, fontWeight: 700, color: anulado ? undefined : '#0369a1' }}>{docTexto(snap[0] || ventas[0])}</span>
                      ) : (
                        <button
                          onClick={() => toggleExpand(r.id)}
                          style={{
                            background: anulado ? '#e2e8f0' : '#eff6ff', color: anulado ? '#64748b' : '#0369a1',
                            border: 'none', borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer'
                          }}
                          title="Incluye ventas del cliente sin abono en esta cobranza"
                        >
                          {ventas.length === snap.length ? `${snap.length} ventas` : `${ventas.length} de ${snap.length} ventas`}
                        </button>
                      )}
                    </td>
                    <td>
                      {anulado ? (
                        <span className="badge-purchase-status" style={{ background: '#f1f5f9', color: '#64748b', border: '1px solid #cbd5e1', textDecoration: 'none' }}>ANULADO</span>
                      ) : (
                        <span className="badge-purchase-status badge-purchase-paid">{r.payment_method_short || r.payment_method_name}</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: anulado ? undefined : '#16a34a', fontSize: 13 }}>{fmtS(r.amount)}</td>
                    <td style={{ fontSize: 11.5, color: anulado ? undefined : '#64748b', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.notes}>{r.notes || '—'}</td>
                    <td style={{ fontSize: 11.5 }}>{r.username || '—'}</td>
                    <td className="td-actions" style={{ textDecoration: 'none' }}>
                      {anulado ? (
                        <span style={{ fontSize: 10.5, color: '#94a3b8', fontStyle: 'italic' }} title={r.anulado_at ? `Anulado el ${fmtDate(r.anulado_at)}` : 'Anulado'}>
                          — histórico —
                        </span>
                      ) : (
                        <>
                          <button className="btn-salir" style={{ padding: '3px 8px', fontSize: 10.5 }} title="Reimprimir recibo" onClick={() => handleReimprimir(r)}>
                            <FiPrinter size={11} />
                          </button>
                          <button
                            className="btn-salir"
                            style={{ padding: '3px 8px', fontSize: 10.5, color: '#dc2626', borderColor: '#fecaca', marginLeft: 4 }}
                            title="Eliminar cobranza"
                            disabled={deleting === r.id}
                            onClick={() => handleEliminar(r)}
                          >
                            <FiTrash2 size={11} />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                  {expandible && expanded && (
                    <tr className={i % 2 === 0 ? 'mt-row-even' : 'mt-row-odd'}>
                      <td></td>
                      <td colSpan={9} style={{ padding: '4px 12px 10px' }}>
                        <table className="mt-table" style={{ fontSize: 11.5, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6 }}>
                          <thead>
                            <tr className="mt-head-row">
                              <th>Fecha Venta</th><th>Documento</th>
                              <th style={{ textAlign: 'center' }}>Cuota</th>
                              <th style={{ textAlign: 'right' }}>Deuda</th>
                              <th style={{ textAlign: 'right' }}>Cobrado</th>
                            </tr>
                          </thead>
                          <tbody>
                            {snap.map((v, vi) => {
                              const noAfectada = !(parseFloat(v.monto_cobrado) > 0);
                              return (
                                <tr key={v.ar_id ?? v.id ?? vi} style={noAfectada ? { color: '#94a3b8' } : {}}>
                                  <td>{fmtDate(v.sale_date)}</td>
                                  <td style={{ fontFamily: 'monospace', fontWeight: 700, color: noAfectada ? undefined : '#0369a1' }}>{docTexto(v)}</td>
                                  <td style={{ textAlign: 'center' }}>
                                    <span style={{ background: noAfectada ? '#cbd5e1' : '#0ea5e9', color: '#fff', fontWeight: 700, fontSize: 10, padding: '2px 8px', borderRadius: 20 }}>
                                      CUOTA {v.cuota_num}
                                    </span>
                                  </td>
                                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtS(v.saldo_anterior ?? v.amount)}</td>
                                  <td style={{ textAlign: 'right', fontWeight: 700, color: noAfectada ? '#94a3b8' : '#16a34a' }}>{noAfectada ? 'Sin abono' : fmtS(v.monto_cobrado)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {reprintTicket && (
        <TicketCobroResult
          ticket={reprintTicket}
          empresa={empresa}
          onCerrar={() => setReprintTicket(null)}
          title="REIMPRESIÓN DE COBRO"
          showBanner={false}
        />
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ════════════════════════════════════════════════════════════
export default function CobranzasMaintenance({ businessId }) {
  const [tab, setTab] = useState('deudas'); // 'deudas' | 'realizadas'
  const [clients, setClients] = useState([]);
  const [empresa, setEmpresa] = useState({ name: 'PIVASAPOLLOS', ruc: '20100000001', address: 'AV. BENAVIDES NRO 6600-6606, MIRAFLORES - LIMA' });
  const [cobroModal, setCobroModal] = useState(null); // ar id
  const [showNuevaCobranza, setShowNuevaCobranza] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!businessId) return;
    getClients(businessId).then(r => setClients(r.data?.data || []));
    getEmpresaInfo(businessId).then(r => { if (r.data?.data) setEmpresa(r.data.data); }).catch(() => {});
  }, [businessId]);

  return (
    <div className="mt-container">
      <div className="mt-toolbar" style={{ borderBottom: 'none', paddingBottom: 0 }}>
        <div className="mt-toolbar-left"><span className="mt-title">COBRANZAS</span></div>
        <button className="btn-nuevo" onClick={() => setShowNuevaCobranza(true)}>
          <FiDollarSign size={13} /> Nueva Cobranza
        </button>
      </div>

      <div className="si-filter-bar" style={{ paddingTop: 0 }}>
        <div className="si-filter-group">
          <button
            className={tab === 'deudas' ? 'btn-nuevo' : 'btn-salir'}
            onClick={() => setTab('deudas')}
          >
            Deudas por Cliente
          </button>
          <button
            className={tab === 'realizadas' ? 'btn-nuevo' : 'btn-salir'}
            onClick={() => setTab('realizadas')}
          >
            Cobranzas Realizadas
          </button>
        </div>
      </div>

      {tab === 'deudas' && (
        <TabDeudas
          key={`deudas-${refreshKey}`}
          businessId={businessId}
          empresa={empresa}
          clients={clients}
          onOpenCobro={setCobroModal}
        />
      )}
      {tab === 'realizadas' && (
        <TabRealizadas
          key={`realizadas-${refreshKey}`}
          businessId={businessId}
          empresa={empresa}
          clients={clients}
        />
      )}

      {cobroModal && (
        <ModalCobro
          arId={cobroModal}
          businessId={businessId}
          empresa={empresa}
          onClose={() => setCobroModal(null)}
          onCobrado={() => { setCobroModal(null); setRefreshKey(k => k + 1); }}
        />
      )}

      {showNuevaCobranza && (
        <ModalNuevaCobranza
          businessId={businessId}
          empresa={empresa}
          clients={clients}
          onClose={() => setShowNuevaCobranza(false)}
          onCobrado={() => { setShowNuevaCobranza(false); setRefreshKey(k => k + 1); }}
        />
      )}
    </div>
  );
}
